import { createContext, type ReactNode, useContext } from 'react';

import type {
	Edition,
	Environment,
	PremiumWrapperProps,
	Product,
	SiteType,
	UserRole,
} from '../types';

import type { Config } from './use-config';

export interface EditionAwarenessContextType {
	//  ------------------------- Required properties -------------------------
	cloudId: string;
	product: Product;
	edition: Edition;
	config: Config;

	//  ------------------------- Payment properties --------------------------
	/**
	 * If this is null, then we cannot say that the entitlement is invoiceable. Please consider not showing the experience to customers
	 * in this case as they may have payment details already.
	 */
	isInvoiceable?: boolean | null;
	canAddPaymentDetails?: boolean;
	addPaymentDetailsExperienceUrl?: string | null;

	//  --------------------------- Trial properties ---------------------------
	trialDaysLeft?: number;
	trialLengthDays?: number;
	trialStartDate?: Date;
	trialEndDate?: Date;
	isInTrial?: boolean;
	isInPredunning?: boolean;
	isInReverseTrial?: boolean;
	trialOfferingName?: Edition;
	changeOfferingExperienceUrl?: string | null;

	//  ---------------------- Entitlement properties ----------------------
	isInTeamWorkCollection?: boolean;

	//  ---------------------- User permissions properties ----------------------
	canUpgrade?: boolean;
	userRoles?: UserRole[];
	userEmail?: string | null;

	//  ---------------------------- Site properties ----------------------------
	/**
	 * This site type is calculated on a trait, when the trait is not loaded is undefined,
	 * if the flag is disabled or no trait is found it will be null.
	 * If the trait is found and the value return an unexpected value it will be 'UNKNOWN'
	 * If the trait is found and the value is 'EDU' it will be 'EDU'
	 * If the trait is found and the value is 'ORG' it will be 'ORG'
	 */
	siteType?: SiteType;
	navVersion?: string;
	billingSourceSystem?: 'CCP' | 'HAMS';
	environment?: Environment;
	licensedUsers?: number;
	storageUsageInGB?: number;

	//  ------------------------- Capabilities  -------------------------
	//  ---- Dismissable button ----
	hasDismissedButton?: boolean;
	isSpotlightActive?: boolean;

	//  ---- Spotlight ----
	onDismissButton?: () => void;
	handleSetSpotlight?: (isActive: boolean) => void;

	//  ---- RT Dropdown Premium Features ----
	onOpenOnboardingMenu?: () => void;
	premiumFeatureHighlightToggle?: ReactNode;
	shouldAutoOpen?: boolean;
	onAutoOpen?: () => void;
	/**
	 * Confluence in premium has a lot of specific packages that are wired up with the button,
	 * we're therefore passing it through to edition awareness to prevent fragmenting the implementation
	 * of those features (e.g. needing to replicate/shift the logic for button border nudges into platform)
	 */
	PremiumWrapper?: (props: PremiumWrapperProps) => ReactNode;
}

export const EditionAwarenessContext = createContext<EditionAwarenessContextType | null>(null);

export function useEditionAwareness(): EditionAwarenessContextType {
	const context = useContext(EditionAwarenessContext);

	if (!context) {
		throw new Error('Missing context, please ensure this is used within EditionAwarenessContext');
	}

	return context;
}
